import React from 'react';
import {
  GridColumnMenu,
  GridColumnMenuProps,
  GridColumnMenuItemProps,
  GridPinnedColumnFields,
  GridPinnedColumnPosition,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';

interface PinnedColumnMenuItemProps extends GridColumnMenuItemProps {
  pinnedColumns: GridPinnedColumnFields;
}

const PinnedColumnMenuPinningItem = ({
  colDef,
  onClick,
  pinnedColumns,
}: PinnedColumnMenuItemProps) => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();

  const field = colDef.field;
  const side = apiRef.current.isColumnPinned(field);

  const leftCount = pinnedColumns.left?.length ?? 0;
  const rightCount = pinnedColumns.right?.length ?? 0;

  const pinColumn =
    (targetSide: GridPinnedColumnPosition) => (event: React.MouseEvent) => {
      apiRef.current.pinColumn(field, targetSide);
      onClick(event);
    };

  const unpinColumn = (event: React.MouseEvent) => {
    apiRef.current.unpinColumn(field);
    onClick(event);
  };

  if (!colDef) return null;

  if (side) {
    const otherSide =
      side === GridPinnedColumnPosition.RIGHT
        ? GridPinnedColumnPosition.LEFT
        : GridPinnedColumnPosition.RIGHT;

    const label =
      otherSide === GridPinnedColumnPosition.RIGHT ? 'pinToRight' : 'pinToLeft';
    const Icon =
      side === GridPinnedColumnPosition.RIGHT
        ? rootProps.slots.columnMenuPinLeftIcon
        : rootProps.slots.columnMenuPinRightIcon;

    const isOtherSideDisabled =
      otherSide === GridPinnedColumnPosition.LEFT
        ? leftCount >= MAX_PINNED_COLUMNS
        : rightCount >= MAX_PINNED_COLUMNS;

    return (
      <>
        <rootProps.slots.baseMenuItem
          onClick={isOtherSideDisabled ? undefined : pinColumn(otherSide)}
          disabled={isOtherSideDisabled}
          iconStart={<Icon fontSize='small' />}
        >
          {apiRef.current.getLocaleText(label)}
        </rootProps.slots.baseMenuItem>
        <rootProps.slots.baseMenuItem
          onClick={unpinColumn}
          iconStart={<span />}
        >
          {apiRef.current.getLocaleText('unpin')}
        </rootProps.slots.baseMenuItem>
      </>
    );
  }

  const isLeftDisabled = leftCount >= MAX_PINNED_COLUMNS;
  const isRightDisabled = rightCount >= MAX_PINNED_COLUMNS;

  return (
    <>
      <rootProps.slots.baseMenuItem
        onClick={
          isLeftDisabled ? undefined : pinColumn(GridPinnedColumnPosition.LEFT)
        }
        disabled={isLeftDisabled}
        iconStart={<rootProps.slots.columnMenuPinLeftIcon fontSize='small' />}
      >
        {apiRef.current.getLocaleText('pinToLeft')}
      </rootProps.slots.baseMenuItem>
      <rootProps.slots.baseMenuItem
        onClick={
          isRightDisabled
            ? undefined
            : pinColumn(GridPinnedColumnPosition.RIGHT)
        }
        disabled={isRightDisabled}
        iconStart={<rootProps.slots.columnMenuPinRightIcon fontSize='small' />}
      >
        {apiRef.current.getLocaleText('pinToRight')}
      </rootProps.slots.baseMenuItem>
    </>
  );
};

interface PinnedColumnMenuProps extends GridColumnMenuProps {
  pinnedColumns: GridPinnedColumnFields;
}

export const PinnedColumnMenu = ({
  pinnedColumns,
  ...props
}: PinnedColumnMenuProps) => {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuPinningItem: (slotProps: GridColumnMenuItemProps) => (
          <PinnedColumnMenuPinningItem
            {...slotProps}
            pinnedColumns={pinnedColumns}
          />
        ),
      }}
    />
  );
};
